import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const INVENTORY_QUERY_KEY = ["inventory", "me"];

/** ───── API client ───── */
export const inventoryApi = {
  get: () => api.get("/inventory/me").then((r) => r.data),
  put: (payload) => api.put("/inventory/me", payload).then((r) => r.data),
  toggle: (payload) => api.post("/inventory/me/toggle", payload).then((r) => r.data),
  updateProfile: (display_name) =>
    api.patch("/inventory/me/profile", { display_name }).then((r) => r.data),
};

/** ───── Hooks ───── */
export function useInventory() {
  return useQuery({
    queryKey: INVENTORY_QUERY_KEY,
    queryFn: inventoryApi.get,
    staleTime: 30_000,
  });
}

export function useToggleSticker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryApi.toggle,
    onMutate: async ({ number, list }) => {
      await qc.cancelQueries({ queryKey: INVENTORY_QUERY_KEY });
      const prev = qc.getQueryData(INVENTORY_QUERY_KEY);
      if (prev) {
        const other = list === "duplicates" ? "missing" : "duplicates";
        const targetSet = new Set(prev[list]);
        const otherSet = new Set(prev[other]);
        if (targetSet.has(number)) {
          targetSet.delete(number);
        } else {
          targetSet.add(number);
          otherSet.delete(number);
        }
        const next = {
          ...prev,
          [list]: [...targetSet].sort((a, b) => a - b),
          [other]: [...otherSet].sort((a, b) => a - b),
        };
        next.stats = {
          duplicates_count: next.duplicates.length,
          missing_count: next.missing.length,
          completion_pct:
            Math.round(((650 - next.missing.length) / 650) * 1000) / 10,
        };
        qc.setQueryData(INVENTORY_QUERY_KEY, next);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(INVENTORY_QUERY_KEY, ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(INVENTORY_QUERY_KEY, data),
  });
}

export function usePutInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryApi.put,
    onSuccess: (data) => qc.setQueryData(INVENTORY_QUERY_KEY, data),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryApi.updateProfile,
    onSuccess: (data) => qc.setQueryData(INVENTORY_QUERY_KEY, data),
  });
}

/** ───── Quick-add parser ─────
 * Accepts comma-or-whitespace separated tokens.
 * Supports single numbers ("100") and ranges ("100-105", inclusive).
 * Returns { numbers: sorted unique int[], invalid: string[] } — invalid lists
 * tokens that aren't valid numbers/ranges OR that fall outside [1, 650].
 */
export function parseRanges(text) {
  const tokens = (text || "").split(/[,\s]+/).map((t) => t.trim()).filter(Boolean);
  const numbers = new Set();
  const invalid = [];
  for (const tok of tokens) {
    const rangeMatch = /^(\d+)-(\d+)$/.exec(tok);
    if (rangeMatch) {
      const a = Number(rangeMatch[1]);
      const b = Number(rangeMatch[2]);
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      if (lo < 1 || hi > 650) {
        invalid.push(tok);
        continue;
      }
      for (let i = lo; i <= hi; i++) numbers.add(i);
      continue;
    }
    if (/^\d+$/.test(tok)) {
      const n = Number(tok);
      if (n < 1 || n > 650) {
        invalid.push(tok);
        continue;
      }
      numbers.add(n);
      continue;
    }
    invalid.push(tok);
  }
  return {
    numbers: [...numbers].sort((a, b) => a - b),
    invalid,
  };
}
